import { useTranslations } from "@acto/web-common/locales";
import { Kpi } from "@acto/taskpane/core/domain";
import { formatCurrency } from "@acto/taskpane/core/utils/formatters";
import type { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";
import { formatProfitWithMargin } from "../../../utils/formatters";

export type CustomerMetric = "revenue" | "margin" | "orders";

export const CUSTOMER_METRICS: CustomerMetric[] = ["revenue", "margin", "orders"];

// The MARGIN kpi's total is a ratio but its series holds profit, so margin is fetched as PROFIT and
// the ratio is derived from revenue.
export const METRIC_KPI_TYPE = {
  revenue: "REVENUE",
  margin: "PROFIT",
  orders: "ORDERS",
} as const satisfies Record<CustomerMetric, string>;

export const metricTotalKpis = (metric: CustomerMetric): Kpi[] =>
  metric === "margin" ? [Kpi.PROFIT, Kpi.REVENUE] : [Kpi[METRIC_KPI_TYPE[metric]]];

export const METRIC_PRODUCT_KPI: Record<CustomerMetric, Kpi> = {
  revenue: Kpi.REVENUE,
  margin: Kpi.MARGIN,
  orders: Kpi.ORDERS,
};

export const useMetricLabels = (): Record<CustomerMetric, { label: string; totalLabel: string }> => {
  const t = useTranslations();
  return {
    revenue: {
      label: t("managementView.customerDetails.revenue"),
      totalLabel: t("managementView.customerDetails.totalRevenue"),
    },
    margin: {
      label: t("managementView.customerDetails.margin"),
      totalLabel: t("managementView.customerDetails.totalMargin"),
    },
    orders: {
      label: t("managementView.customerDetails.orders"),
      totalLabel: t("managementView.customerDetails.orders"),
    },
  };
};

export const marginRatio = (profit: number | null, revenue: number | null): number | null =>
  profit === null || !revenue ? null : profit / revenue;

export const formatOrderCount = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

/** `revenue` is read only for margin, as the denominator of the bracketed ratio. */
export const formatMetricValue = (
  metric: CustomerMetric,
  value: number,
  revenue: number | null,
  locale: string,
  currency: CurrencyCode,
): string => {
  switch (metric) {
    case "revenue":
      return formatCurrency(value, locale, currency);
    case "margin":
      return formatProfitWithMargin(value, marginRatio(value, revenue), locale, currency);
    case "orders":
      return formatOrderCount(value, locale);
  }
};

export const formatMetricWithoutRatio = (
  metric: CustomerMetric,
  value: number,
  locale: string,
  currency: CurrencyCode,
): string => (metric === "orders" ? formatOrderCount(value, locale) : formatCurrency(value, locale, currency));
